import type React from 'react'
import { useState } from 'react'
import { X } from 'lucide-react'

import { AlertDialog, AlertDialogContent, AlertDialogTrigger } from 'shadcdn/ui/alert-dialog'

interface YoutubeVideoBlockProps {
  url: string
}

export const YoutubeVideoBlock: React.FC<YoutubeVideoBlockProps> = ({ url }) => {
  const [isOpen, setIsOpen] = useState(false)
  const videoId = getVideoId(url)

  if (!videoId) {
    return <div className="text-red-500">Invalid YouTube URL</div>
  }

  const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/0.jpg`

  const handleBackgroundClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      setIsOpen(false)
    }
  }

  return (
    <AlertDialog open={isOpen} onOpenChange={setIsOpen}>
      <AlertDialogTrigger asChild>
        <div className="max-w-72 aspect-video cursor-pointer relative">
          <img src={thumbnailUrl} alt="YouTube video thumbnail" className="w-full h-full object-cover rounded" />
          <div className="absolute inset-0 flex items-center justify-center">
            <svg
              className="w-12 h-12 text-white opacity-80"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        </div>
      </AlertDialogTrigger>
      <AlertDialogContent className="max-w-6xl p-0 bg-black border-0" onOverlayClick={handleBackgroundClick}>
        <div className="relative pb-[56.25%] h-0">
          <iframe
            className="absolute top-0 left-0 w-full h-full"
            src={`https://www.youtube.com/embed/${videoId}?autoplay=1`}
            title="YouTube video player"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
        <button
          onClick={() => setIsOpen(false)}
          className="absolute top-[-1rem] right-[-0.8rem] p-1 rounded-full bg-gray-100 hover:bg-gray-200"
        >
          <X className="w-6 h-6" />
        </button>
      </AlertDialogContent>
    </AlertDialog>
  )
}

export const getVideoId = (url: string): string | null => {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/
  const match = url.match(regExp)
  return match && match[2].length === 11 ? match[2] : null
}
